'use client';

import { cn } from '@core/utils';
import type { CSSProperties, ReactNode } from 'react';

import { BREAKPOINTS } from '@/constants';
import { isBannerAdHidden, useAd } from '@/hooks/useAd';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { AdBadge } from './AdBadge';
import {
  AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  AdSlotDebugPlaceholder,
} from './AdSlotDebugPlaceholder';

//* wrapperClassName이 있을 때만 외곽을 감싼다. 광고가 숨김(null)이면 wrapper도 안 생겨 빈 영역이 남지 않는다.
const SlotWrapper = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) =>
  className ? <div className={className}>{children}</div> : <>{children}</>;

const getSlotFrameStyle = (width: number, height: number): CSSProperties => ({
  width: `min(100%, ${width}px)`,
  aspectRatio: `${width} / ${height}`,
});

interface BannerAdSlotProps {
  //* 빈 string은 콘솔 ID 미발급 슬롯 — 운영에서는 null, 개발에서는 위치 확인용 placeholder.
  unitIdM: string;
  unitIdP: string;
  mWidth: number;
  mHeight: number;
  pWidth: number;
  pHeight: number;
  rounded?: 'medium' | 'large';
  className?: string;
  //* 광고/placeholder가 실제로 렌더될 때만 감싸는 외곽 wrapper (배경·여백·높이 예약 등).
  wrapperClassName?: string;
}

export const BannerAdSlot = ({
  unitIdM,
  unitIdP,
  mWidth,
  mHeight,
  pWidth,
  pHeight,
  rounded = 'medium',
  className,
  wrapperClassName,
}: BannerAdSlotProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const unitId = isDesktop ? unitIdP : unitIdM;
  const width = isDesktop ? pWidth : mWidth;
  const height = isDesktop ? pHeight : mHeight;

  if (!unitId && AD_SLOT_DEBUG_PLACEHOLDER_ENABLED) {
    return (
      <SlotWrapper className={wrapperClassName}>
        <div
          className={cn('relative mx-auto max-w-full', className)}
          style={getSlotFrameStyle(width, height)}
        >
          <AdSlotDebugPlaceholder
            width={width}
            height={height}
            rounded={rounded}
          />
        </div>
      </SlotWrapper>
    );
  }

  if (!unitId) return null;

  return (
    <BannerAdSlotInner
      key={unitId}
      unitId={unitId}
      width={width}
      height={height}
      rounded={rounded}
      className={className}
      wrapperClassName={wrapperClassName}
    />
  );
};

interface BannerAdSlotInnerProps {
  unitId: string;
  width: number;
  height: number;
  rounded: 'medium' | 'large';
  className?: string;
  wrapperClassName?: string;
}

const BannerAdSlotInner = ({
  unitId,
  width,
  height,
  rounded,
  className,
  wrapperClassName,
}: BannerAdSlotInnerProps) => {
  const { containerRef, adState } = useAd(unitId, { type: 'banner' });

  if (isBannerAdHidden(adState) && !AD_SLOT_DEBUG_PLACEHOLDER_ENABLED) {
    return null;
  }

  if (isBannerAdHidden(adState)) {
    return (
      <SlotWrapper className={wrapperClassName}>
        <div
          className={cn('relative mx-auto max-w-full', className)}
          style={getSlotFrameStyle(width, height)}
        >
          <AdSlotDebugPlaceholder
            width={width}
            height={height}
            rounded={rounded}
          />
        </div>
      </SlotWrapper>
    );
  }

  return (
    <SlotWrapper className={wrapperClassName}>
      <div
        className={cn('relative mx-auto max-w-full', className)}
        style={getSlotFrameStyle(width, height)}
      >
        <AdBadge
          className={cn(
            'right-12 top-12',
            adState !== 'adReceived' && 'invisible',
          )}
        />
        <div
          ref={containerRef}
          className={cn(
            'size-full overflow-hidden bg-Gray-100 [&>*]:size-full [&_iframe]:size-full',
            rounded === 'large' ? 'rounded-large' : 'rounded-medium',
            adState === 'idle' && 'invisible',
            adState === 'loading' && 'animate-pulse',
          )}
        />
      </div>
    </SlotWrapper>
  );
};
